import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { adminNavItems, patientNavItems, physicianNavItems, sponsorNavItems } from '../navigation/navItems';

/**
 * Keyboard Shortcuts Hook
 * Provides keyboard navigation for power users
 * 
 * Shortcuts:
 * - Ctrl/Cmd + K: Open search/command palette (future)
 * - Alt + 1-9: Quick navigation to first 9 nav items
 * - Escape: Close modals/dropdowns
 */
export const useKeyboardShortcuts = (options = {}) => {
  const navigate = useNavigate();
  const { profile, isAdmin, isProvider } = useAuth();
  const { enabled = true, onSearch } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleKeyPress = (e) => {
      // Ctrl/Cmd + K for search (future command palette)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (onSearch) {
          onSearch();
        } else {
          // Default: focus search if available
          const searchInput = document.querySelector('input[type="search"], input[placeholder*="Search"]');
          if (searchInput) {
            searchInput.focus();
          }
        }
        return;
      }

      // Alt + 1-9 for quick navigation
      if (e.altKey && /^[1-9]$/.test(e.key)) {
        e.preventDefault();
        
        // Get navigation items based on role
        let navItems = [];
        if (isAdmin) {
          navItems = adminNavItems;
        } else if (isProvider) {
          navItems = physicianNavItems;
        } else if (profile?.tenant_role === 'sponsor') {
          navItems = sponsorNavItems;
        } else {
          navItems = patientNavItems;
        }

        // Filter out items with children (use first child) and get flat list
        const flatNavItems = [];
        navItems.forEach((item) => {
          if (item.path && item.path !== '#') {
            flatNavItems.push(item);
          }
          if (item.children) {
            item.children.forEach((child) => {
              if (child.path && child.path !== '#') {
                flatNavItems.push(child);
              }
            });
          }
        });

        // Get item at index (1-9 maps to 0-8)
        const index = parseInt(e.key) - 1;
        const targetItem = flatNavItems[index];

        if (targetItem && targetItem.path) {
          navigate(targetItem.path);
        }
        return;
      }

      // Escape key: close modals/dropdowns
      if (e.key === 'Escape') {
        // Close any open dropdowns or modals
        const activeDropdown = document.querySelector('[role="menu"][aria-expanded="true"]');
        const activeModal = document.querySelector('[role="dialog"][aria-hidden="false"]');
        
        if (activeDropdown) {
          const closeButton = activeDropdown.querySelector('button[aria-label*="close"], button[aria-label*="Close"]');
          if (closeButton) {
            closeButton.click();
          }
        }
        
        if (activeModal) {
          const closeButton = activeModal.querySelector('button[aria-label*="close"], button[aria-label*="Close"]');
          if (closeButton) {
            closeButton.click();
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [enabled, navigate, isAdmin, isProvider, profile, onSearch]);
};

export default useKeyboardShortcuts;

